import { useParams, Link } from 'react-router-dom';
import { categories, movies } from '@/mocks/movies';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieGrid from './components/MovieGrid';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryMovies = movies.filter((m) => m.category === slug);

  if (!category) {
    return (
      <main className="pt-16">
        <div className="flex flex-col items-center justify-center py-20">
          <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4"></i>
          <h2 className="font-heading text-xl font-bold text-foreground-700 mb-2">分类不存在</h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-16">
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: category.name },
        ]}
      />

      <section className="px-4 md:px-6 py-6 md:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-100/70 text-primary-500">
              <i className={`${category.icon} text-2xl`}></i>
            </div>
            <div>
              <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-1">{category.name}</h1>
              <p className="text-sm text-foreground-600">{category.description}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryMovies.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {categoryMovies.map((movie) => (
                <Link
                  key={movie.id}
                  to={`/article/${movie.id}`}
                  className="group block cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-xl bg-background-100 mb-3 aspect-[3/4]">
                    <img
                      src={movie.coverImage}
                      alt={movie.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-0.5 text-[10px] font-medium bg-primary-500 text-background-50 rounded-md whitespace-nowrap">
                        {movie.quality}
                      </span>
                    </div>
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 text-[10px] font-medium bg-accent-500 text-background-50 rounded-md whitespace-nowrap">
                        {movie.rating} 分
                      </span>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 pt-10">
                      <div className="flex items-center gap-2 text-background-50 text-xs">
                        <i className="ri-play-fill"></i>
                        <span>{movie.playCount} 次播放</span>
                      </div>
                    </div>
                  </div>
                  <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                    {movie.title}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <span>{movie.year}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                    <span>{movie.duration}</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <i className="ri-film-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">该分类暂时没有内容，敬请期待</p>
            </div>
          )}
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${category.name} - 90影视`,
            "description": category.description,
            "url": `https://www.90yingshi.com/category/${slug}`,
            "isPartOf": {
              "@type": "WebSite",
              "name": "90影视",
              "url": "https://www.90yingshi.com/"
            }
          })
        }}
      />
    </main>
  );
}